import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Paper, Skeleton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import PanelEmptyState from '../../../Components/PanelEmptyState';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../../hooks/useScrollEdgeFade';
import { useLocatedLot } from '../../hooks/useLocatedLot';
import PanelShell from '../PanelShell/PanelShell';
import PanelHeader, { panelCloseIconButtonSx } from '../PanelShell/PanelHeader';
import RecordCountsLabel from '../RecordCountsLabel';
import ListSortMenu from '../ListSortMenu/ListSortMenu';
import { recordCountsRowSx } from '../UserLotListsPanel/UserLotListsPanel.styles';
import FilterResultsLotCard from './FilterResultsLotCard';
import {
  cardsStackSx,
  filterResultCardActionsSx,
  filterResultCardHeaderSx,
  filterResultCardSx,
  headerZoneSx,
  moreHintSx,
  panelPaperSx,
  refreshingSpinnerSx,
  sampleHintSx,
  scrollZoneSx,
  skeletonMutedSx,
  skeletonPrimarySx,
  spinnerSlotSx,
  toolbarBoxSx,
  toolbarZoneSx,
} from './FilterResultsPanel.styles';

const LIST_RENDER_CHUNK = 60;
const SKELETON_CARD_COUNT = 9;

const PANEL_DESCRIPTION = 'Coincidencias de los filtros en la vista actual. Mové el mapa para actualizar.';
const LOADING_LABEL = 'Calculando resultados según la posición actual…';

/** Mismas dos opciones de siempre; formato de `ListSortMenu`. */
export const FILTER_RESULTS_SORT_OPTIONS = [
  { value: 'surface', label: 'Mayor superficie' },
  { value: 'valuation', label: 'Mayor valuación' },
];

/**
 * Mayor primero. Sin dato, al final. Desempate: el otro criterio y el catastral.
 * @param {object[]} lots
 * @param {'surface'|'valuation'} sortBy
 */
export function sortFilterResultLots(lots, sortBy) {
  const compareDesc = (a, b) => {
    const aOk = a != null && Number.isFinite(a);
    const bOk = b != null && Number.isFinite(b);
    if (!aOk && !bOk) return 0;
    if (!aOk) return 1;
    if (!bOk) return -1;
    return b - a;
  };

  return [...lots].sort((a, b) => {
    const primary = sortBy === 'valuation'
      ? compareDesc(a.val_b, b.val_b)
      : compareDesc(a.urbansurface, b.urbansurface);
    if (primary !== 0) return primary;
    const secondary = sortBy === 'valuation'
      ? compareDesc(a.urbansurface, b.urbansurface)
      : compareDesc(a.val_b, b.val_b);
    if (secondary !== 0) return secondary;
    return String(a.lot_name || '').localeCompare(String(b.lot_name || ''), 'es');
  });
}

/** Skeleton de las cards: estructura de `RecordLotIdentity` (título, lugar, datos) + ⋮. */
const LoadingFilterResultCards = ({ count = SKELETON_CARD_COUNT }) => (
  <Box
    sx={cardsStackSx}
    aria-busy="true"
    aria-label="Cargando coincidencias de esta vista"
    data-testid="filter-results-cards-skeleton"
  >
    {Array.from({ length: count }).map((_, idx) => (
      <Box key={idx} sx={{ ...filterResultCardSx, cursor: 'default', '&:hover': {} }}>
        <Box sx={filterResultCardHeaderSx}>
          <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Skeleton variant="rounded" width={`${62 - (idx % 3) * 6}%`} height={18} sx={skeletonPrimarySx} />
            <Box sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
              <Skeleton variant="rounded" width={`${44 - (idx % 3) * 5}%`} height={14} sx={skeletonMutedSx} />
            </Box>
            <Skeleton variant="rounded" width={idx % 2 === 0 ? 168 : 132} height={12} sx={skeletonMutedSx} />
          </Box>
          <Box sx={filterResultCardActionsSx}>
            <Skeleton variant="circular" width={18} height={18} sx={skeletonPrimarySx} />
          </Box>
        </Box>
      </Box>
    ))}
  </Box>
);

/**
 * Listado opt-in de parcelas filtradas que están dibujadas en el viewport.
 * No reemplaza Registros ni el detalle de lista: esos paneles ya son la lista.
 * Ver `docs/maps/filter-results.md`.
 */
export default function FilterResultsPanel({
  uiLeftInset,
  hidden = false,
  lots = [],
  truncated = false,
  isSampleView = false,
  isRefreshing = false,
  hasSettled = false,
  onClose,
  onOpenLot,
  onLocateLot,
  /** Ids a pintar en el mapa (el lote ubicado o `[]`): mismo resaltado que Registros. */
  onHighlightLotIdsChange,
}) {
  const [visibleCount, setVisibleCount] = useState(LIST_RENDER_CHUNK);
  const [sortBy, setSortBy] = useState('surface');
  const lotCountRef = useRef(lots.length);
  lotCountRef.current = lots.length;
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();
  // Ubicar hace lo mismo que el ícono de antes (`onLocateLot(lotId, lot)`);
  // además la card queda marcada y el lote pintado (abajo).
  const locateOnMap = useCallback((lotId, lot) => onLocateLot?.(lotId, lot), [onLocateLot]);
  const {
    locatedLotId,
    toggleLocatedLot,
    clearLocatedLot,
    isLotLocated,
  } = useLocatedLot({ onLocate: locateOnMap });

  const sortedLots = useMemo(
    () => sortFilterResultLots(lots, sortBy),
    [lots, sortBy],
  );

  useEffect(() => {
    setVisibleCount(LIST_RENDER_CHUNK);
  }, [lots, sortBy]);

  const visibleLots = useMemo(
    () => sortedLots.slice(0, visibleCount),
    [sortedLots, visibleCount],
  );

  const handleListScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 400) return;
    setVisibleCount((count) => (
      count >= lotCountRef.current ? count : count + LIST_RENDER_CHUNK
    ));
  }, []);

  const isLoading = isRefreshing || !hasSettled;
  const showEmpty = !isLoading && lots.length === 0;
  const showList = !isLoading && lots.length > 0;

  // El lote ubicado se pinta en el mapa (como Registros); al quitar la marca,
  // ubicar otro o cerrar el panel, se despinta.
  const onHighlightRef = useRef(onHighlightLotIdsChange);
  onHighlightRef.current = onHighlightLotIdsChange;
  useEffect(() => {
    onHighlightRef.current?.(locatedLotId ? [locatedLotId] : []);
  }, [locatedLotId]);
  useEffect(() => () => onHighlightRef.current?.([]), []);

  // Si al terminar de recalcular la vista el lote ya no está entre los
  // resultados (salió del recorte o de los filtros), la marca se va. Mientras
  // recalcula no: ubicar mueve el mapa y dispara un recálculo.
  useEffect(() => {
    if (!locatedLotId || isLoading) return;
    if (lots.some((lot) => String(lot.lot_id) === locatedLotId)) return;
    clearLocatedLot();
  }, [lots, isLoading, locatedLotId, clearLocatedLot]);

  const countsLabel = truncated
    ? `${lots.length} en esta vista · hay más fuera de este recorte`
    : `${lots.length} en esta vista`;

  let content;
  if (isLoading) {
    content = <LoadingFilterResultCards />;
  } else if (showEmpty) {
    content = (
      <PanelEmptyState
        testId="filter-results-empty-state"
        icon={FilterListOutlinedIcon}
        title={isSampleView ? 'Acercate para ver coincidencias' : 'No hay coincidencias en esta vista'}
        hint={isSampleView
          ? 'A este zoom el mapa muestra una muestra de puntos. Acercate o mové el encuadre.'
          : 'Mové el mapa o cambiá los filtros. Solo se listan los lotes cargados en pantalla.'}
        sx={{ minHeight: 0, py: 0 }}
      />
    );
  } else {
    content = (
      <Box sx={{ flexShrink: 0 }}>
        <Box component="ul" sx={{ ...cardsStackSx, listStyle: 'none', m: 0, p: 0 }}>
          {visibleLots.map((lot) => (
            <FilterResultsLotCard
              key={lot.lot_id}
              lot={lot}
              locateActive={isLotLocated(lot.lot_id)}
              onOpenLot={onOpenLot}
              onLocateLot={toggleLocatedLot}
            />
          ))}
        </Box>
        {sortedLots.length > visibleLots.length && (
          <Typography sx={moreHintSx}>
            Mostrando {visibleLots.length} de {sortedLots.length} · seguí desplazándote para ver más
          </Typography>
        )}
      </Box>
    );
  }

  const showToolbar = isLoading || showList;

  return (
    <PanelShell uiLeftInset={uiLeftInset} onClose={onClose} hidden={hidden}>
      <Paper elevation={0} sx={panelPaperSx}>
        <Box sx={headerZoneSx}>
          <PanelHeader
            title="RESULTADOS"
            subtitle={PANEL_DESCRIPTION}
            actions={(
              <IconButton
                onClick={onClose}
                size="small"
                aria-label="Cerrar resultados"
                sx={panelCloseIconButtonSx}
              >
                <CloseIcon />
              </IconButton>
            )}
          />
        </Box>

        {showToolbar && (
          <Box sx={toolbarZoneSx}>
            <Box sx={toolbarBoxSx}>
              <Box sx={recordCountsRowSx} data-testid="filter-results-counts-row">
                <RecordCountsLabel>{isLoading ? LOADING_LABEL : countsLabel}</RecordCountsLabel>
                {showList ? (
                  <ListSortMenu
                    value={sortBy}
                    onChange={setSortBy}
                    options={FILTER_RESULTS_SORT_OPTIONS}
                    registrosStyle
                  />
                ) : (
                  <Box sx={spinnerSlotSx}>
                    <CircularProgress
                      size={14}
                      thickness={5}
                      sx={refreshingSpinnerSx}
                      aria-label={LOADING_LABEL}
                    />
                  </Box>
                )}
              </Box>
              {showList && isSampleView && (
                <Typography sx={sampleHintSx}>
                  A este zoom los puntos son referenciales. Acercate para ver más.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <Box
          ref={scrollRef}
          onScroll={handleListScroll}
          data-testid="filter-results-scroll"
          sx={{
            ...scrollZoneSx,
            pt: showToolbar ? 1.5 : 1,
            ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
          }}
        >
          {content}
        </Box>
      </Paper>
    </PanelShell>
  );
}
